import {EventDropArg} from '@fullcalendar/core/index.js';
import {EventResizeDoneArg} from '@fullcalendar/interaction/index.js';
import {endOfMonth, formatISO, startOfMonth} from 'date-fns';
import {useApiariesStore} from 'entities/apiaries';
import {useCalendarStore} from 'entities/calendar';
import {CalendarGetRequestParams, CalendarPatchRequestParams} from 'entities/calendar/model/types';
import {useHivesStore} from 'entities/hives';
import {useEffect, useRef, useState} from 'react';
import {useDebounceCallback} from 'shared/lib/debounce/useDebounce';
import {pickerDateToUtcIso} from 'features/calendar/helpers/dates.ts';

type TFilters = {
  date?: Date;
  hiveIds?: string[];
};

type hiveListItem = {
  id: string;
  name: string;
  isSelected: boolean;
};

export const useCalendar = () => {
  const getEventsFromServer = useCalendarStore((s) => s.getEventsFromServer);
  const events = useCalendarStore((s) => s.events);
  const apiaryId = useApiariesStore((state) => state.currentApiaryId);
  const hives = useHivesStore((s) => s.hives);

  const editEvent = useCalendarStore((s) => s.editEvent);

  const [isNewEventOpen, setIsNewEventOpen] = useState<boolean>(false);
  const [isDateDrawerOpen, setIsDateDrawerOpen] = useState<boolean>(false);
  const [isHiveDrawerOpen, setIsHiveDrawerOpen] = useState<boolean>(false);
  const [hiveListItems, setHiveListItems] = useState<hiveListItem[]>([]);
  const [activeDate, setActiveDate] = useState<Date>(new Date());

  const lastFilters = useRef<Partial<CalendarGetRequestParams>>({});

  const getEventsFromServerDebounced = useDebounceCallback(getEventsFromServer, 500);

  const onChangeFilters = async (filters: TFilters) => {
    const payload: Partial<CalendarGetRequestParams> = {};
    if (filters.date) {
      setActiveDate(filters.date);

      const firstDayOfMonth = startOfMonth(filters.date);
      const lastDayOfMonth = endOfMonth(filters.date);

      payload.from = formatISO(firstDayOfMonth);
      payload.to = formatISO(lastDayOfMonth);
    }
    if (filters.hiveIds) {
      payload.hiveIds = filters.hiveIds;
    }

    lastFilters.current = {
      ...lastFilters.current,
      ...payload,
    };

    if (apiaryId) {
      getEventsFromServerDebounced({apiaryId, ...lastFilters.current});
    }
  };

  const onHiveListItemClick = (id: string) => {
    setHiveListItems(
      hiveListItems.map((el) => {
        if (el.id === id) {
          el.isSelected = !el.isSelected;
        }

        return el;
      })
    );

    onChangeFilters({hiveIds: hiveListItems.filter((el) => el.isSelected).map((el) => el.id)});
  };

  const onEventDrop = async (arg: EventDropArg) => {
    const id = arg.event.extendedProps.id;
    if (!id) return;

    const newStart = arg.event.start;
    const newEnd = arg.event.end;

    if (!newStart || !newEnd) return;

    const params: CalendarPatchRequestParams = {
      id,
      startAt: pickerDateToUtcIso(newStart),
      endAt: pickerDateToUtcIso(newEnd),
    };

    await editEvent(params);

    if (apiaryId) {
      getEventsFromServer({apiaryId, ...lastFilters.current});
    }
  };

  const onEventResize = async (arg: EventResizeDoneArg) => {
    const id = arg.event.extendedProps.id;
    if (!id) return;

    const newStart = arg.event.start;
    const newEnd = arg.event.end;

    if (!newStart || !newEnd) return;

    const params: CalendarPatchRequestParams = {
      id,
      startAt: pickerDateToUtcIso(newStart),
      endAt: pickerDateToUtcIso(newEnd),
    };

    await editEvent(params);

    if (apiaryId) {
      getEventsFromServer({apiaryId, ...lastFilters.current});
    }
  };

  useEffect(() => {
    setHiveListItems(hives.map((el) => ({id: el.id, name: `№${el.number}`, isSelected: false})));
  }, [hives]);

  return {
    events,
    isNewEventOpen,
    activeDate,
    isDateDrawerOpen,
    isHiveDrawerOpen,
    hiveListItems,
    setIsNewEventOpen,
    onChangeFilters,
    setIsDateDrawerOpen,
    setIsHiveDrawerOpen,
    onHiveListItemClick,
    onEventDrop,
    onEventResize,
  };
};
